import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import { cleanEmptyParams } from "@/utilities/etc"
import {
  ExpectedError,
  GatedConfirmSetupIntentError,
} from "@/utilities/expectedErrors"
import { gatedSetupIntentQueryOptions } from "@/utilities/hooks/queries/gated"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { useTheme } from "@/utilities/hooks/useTheme"
import { log } from "@/utilities/logging"

import PayingCashLauncher from "../../-components/PayingCash/PayingCashLauncher"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/checkout"
)({
  staticData: {
    pageName: "Checkout",
  },
  validateSearch: (
    search: Record<string, unknown>
  ): { paymentStatus?: "failed" } => {
    return cleanEmptyParams({
      paymentStatus:
        (search.paymentStatus as string) === "failed" ? "failed" : undefined,
    })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { reregister, express } = Route.useSearch()
  const { qrLookupID, sessionID } = Route.useParams()
  const { t } = useTranslation(["gated"])
  const { metadata } = useTheme()
  const cashPaymentMode = metadata?.gated?.payment?.cash?.mode

  const { data } = useSuspenseQuery(gatedSetupIntentQueryOptions({ express }))

  const baseUrl = window.location.origin
  const returnURL = `${baseUrl}/qr/${qrLookupID}/gated/register/${sessionID}/payment-status`

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.checkout.header", { ns: "gated" })}
      </Heading>
      <Step step={4} of={4} />
      <StripeElementsWrapper
        clientSecret={data.clientSecret}
        customerSessionClientSecret={
          data.customerSessionClientSecret || undefined
        }
        flow="gated:register"
      >
        <Card>
          <Heading className="center" level={1} as={2}>
            {t("register.checkout.subhead", { ns: "gated" })}
          </Heading>
          <CheckoutForm
            sessionID={sessionID}
            returnURL={returnURL}
            goNext={(key: string) =>
              navigate({
                to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
                search: { key, express },
              })
            }
          />
        </Card>
      </StripeElementsWrapper>
      {cashPaymentMode === "visible" && !reregister && (
        <PayingCashLauncher
          sessionID={sessionID}
          onSuccess={() =>
            navigate({
              to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
              search: { express },
            })
          }
        />
      )}
    </>
  )
}

interface CheckoutFormProps {
  goNext: (key: string) => void
  returnURL: string
  sessionID: string
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const { t } = useTranslation(["gated", "errors", "common"])
  const { drawer } = useTheme()

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "gated:register",
  })

  if (stripeError) throw stripeError

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement
          onChange={() => setError(undefined)}
          allow-top-navigation
          {...paymentElementProps}
        />
        <Separator
          orientation="horizontal"
          style={{ marginTop: "var(--space-5)" }}
        />
        <div style={{ marginTop: "var(--space-5)" }}>
          <Heading level={4} as={3}>
            {t("register.checkout.totalDue", { ns: "gated" })}
          </Heading>
          <Text level={3} style={{ marginTop: "var(--space-2)" }}>
            {t("register.checkout.totalDueAmount", { ns: "gated" })}{" "}
            <a
              href={
                drawer?.externalLinks?.find((el) => el.type === "rates")
                  ?.link || ""
              }
              target="_blank"
              rel="noreferrer"
            >
              {t("common.seeRates", { ns: "common" })}.
            </a>
          </Text>
        </div>
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}
        <Button type="submit" buttonType="primary" isPending={loading}>
          {t("common.submit", { ns: "common" })}
          <Icon name="arrowRight" slot="right" />
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    if (!stripe || !elements) {
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: props.returnURL,
        },
      })

      if (result.error) {
        log({
          level: "error",
          flow: "gated:register",
          scope: "payment.stripeConfirmSetup",
          message: "Stripe confirmSetup returned an error",
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        return
      }

      const rsp = await api.gated.confirmSetupIntent.POST({})
      if ("failReason" in rsp) {
        throw new GatedConfirmSetupIntentError(rsp.failReason)
      }
      props.goNext(rsp.confirmationKey)
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "gated:register",
        scope: "payment.confirmSetup",
        message: "Gated setup intent confirmation failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
    } finally {
      setLoading(false)
    }
  }
}
